/* eslint-disable react/prop-types */
/* eslint-disable no-console */
import React, { Component } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font
} from "@react-pdf/renderer";

import PoppinsRegular from "../../assets/fonts/Poppins-Regular.ttf";
import PoppinsBold from "../../assets/fonts/Poppins-Bold.ttf";

import Logo from "../../assets/images/remidio@3x.png";
import { convertEpochToDate } from "../../utils/date";

class BiometryPDF extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  // ---------- helpers ----------
  safe = (v, fallback = "") =>
    v === null || v === undefined || Number.isNaN(v) ? fallback : v;

  fmt = (v, digits = 2) => {
    if (v === null || v === undefined || v === "") return "";
    if (typeof v === "string") return v;
    const n = Number(v);
    if (Number.isNaN(n)) return "";
    return n.toFixed(digits);
  };

  // Format biometry values with units
  formatCCT = value => {
    if (value === null || value === undefined || value === 0) return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return `${num.toFixed(2)} μm`;
  };

  formatMM = value => {
    if (value === null || value === undefined || value === 0) return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return `${num.toFixed(2)} mm`;
  };

  formatKeratometryK1 = value => {
    if (value === null || value === undefined || value === "") return "-";
    // If it's already a formatted string with mm and DS, return it
    if (
      typeof value === "string" &&
      value.includes("mm") &&
      value.includes("DS")
    ) {
      return value;
    }
    // If it's a string with multiple values, parse it
    if (typeof value === "string") {
      // Parse format like "7.35/45.15D@168°"
      const parts = value.split("/");
      if (parts.length >= 2) {
        const mm = parseFloat(parts[0]);
        const rest = parts[1]; // "45.15D@168°"

        // Extract diopter and angle from "45.15D@168°"
        const match = rest.match(/([\d.]+)D@(\d+)°/);
        if (match && !isNaN(mm)) {
          const ds = parseFloat(match[1]);
          const angle = parseFloat(match[2]);
          if (!isNaN(ds) && !isNaN(angle)) {
            return `${mm.toFixed(2)} mm /${ds.toFixed(2)} DS @${angle}°`;
          }
        }
      }
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `${num.toFixed(2)} mm`;
      }
      return value;
    }
    const num = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(num)) return "-";
    return `${num.toFixed(2)} mm`;
  };

  formatKeratometryK2 = value => {
    return this.formatKeratometryK1(value);
  };

  formatKeratometryAST = value => {
    if (value === null || value === undefined || value === "") return "-";
    // If it's already a formatted string with DC and @, return it
    if (
      typeof value === "string" &&
      value.includes("DC") &&
      value.includes("@")
    ) {
      return value;
    }
    // If it's a string with multiple values, parse it
    if (typeof value === "string") {
      // Parse format like "-0.65D@168°"
      const match = value.match(/([\d.-]+)D@(\d+)°/);
      if (match) {
        const dc = parseFloat(match[1]);
        const angle = parseFloat(match[2]);
        if (!isNaN(dc) && !isNaN(angle)) {
          return `${dc.toFixed(2)}DC @${angle}°`;
        }
      }
      // Try to parse format like "-0.65/168" or similar
      const parts = value.split("/");
      if (parts.length >= 2) {
        const dc = parseFloat(parts[0]);
        const angle = parseFloat(parts[1]);
        if (!isNaN(dc) && !isNaN(angle)) {
          return `${dc.toFixed(2)}DC @${angle}°`;
        }
      }
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `${num.toFixed(2)}DC`;
      }
      return value;
    }
    const num = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(num)) return "-";
    return `${num.toFixed(2)}DC`;
  };

  formatAvgK = value => {
    if (value === null || value === undefined || value === 0) return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return `${num.toFixed(2)} mm`;
  };

  formatALCR = value => {
    if (value === null || value === undefined || value === 0) return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return num.toFixed(2); // No unit
  };

  formatRefractionS = value => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "string" && value.includes("DS")) {
      return value;
    }
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return `${num.toFixed(2)}DS`;
  };

  formatRefractionC = value => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "string" && value.includes("DC")) {
      return value;
    }
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return `${num.toFixed(2)} DC`;
  };

  formatRefractionA = value => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "string" && value.includes("°")) {
      return value;
    }
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return `${num}°`;
  };

  // returns bold text style if out of nominal range
  outStyle = (val, min, max) => {
    if (val === null || val === undefined || typeof val === "string") return {};
    if (min === null || max === null || min === undefined || max === undefined)
      return {};
    const n = Number(val);
    if (Number.isNaN(n)) return {};
    if (n < min || n > max) return { fontWeight: "bold" };
    return {};
  };

  analysisForAxial = (key, val) => {
    if (val === null || val === undefined || typeof val === "string")
      return "Normal";
    const n = Number(val);
    switch (key) {
      case "cct":
        return "Normal";
      case "ad":
        if (n < 3.0) return "Shallow anterior chamber.";
        if (n > 4.1) return "Deep anterior chamber.";
        return "Normal";
      case "lt":
        if (n < 3.5) return "Lens thinner than typical.";
        if (n > 4.0) return "Lens thicker than typical.";
        return "Normal";
      case "vt":
        return "Normal";
      case "al":
        if (n < 22.0) return "Short axial length.";
        if (n > 25.0) return "Long axial length.";
        return "Normal";
      default:
        return "Normal";
    }
  };

  analysisForK = (key, val) => {
    if (key === "avgR") return "Normal";
    if (key === "ast") {
      if (typeof val === "string") {
        // extract cylinder power like "-1.50D@166°"
        const m = val.match(/(-?\d+(\.\d+)?)D/i);
        if (m) {
          const c = Math.abs(parseFloat(m[1]));
          if (c > 3.0) return "High corneal astigmatism.";
          if (c > 0.5) return "Mild corneal astigmatism.";
        }
      }
      return "Normal";
    }
    return "Normal";
  };

  // ---------- render ----------
  render() {
    const styles = StyleSheet.create({
      page: {
        paddingTop: 14,
        paddingHorizontal: 20,
        paddingBottom: 28,
        fontFamily: "Poppins",
        fontSize: 10,
        color: "#000"
      },
      header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 8
      },
      headerLeft: { flexDirection: "row", alignItems: "center" },
      orgLogoContainer: { maxWidth: 40, maxHeight: 40, marginRight: 10 },
      orgLogo: { objectFit: "contain" },
      orgDetails: { flexDirection: "column" },
      orgName: { fontSize: 12, fontFamily: "Poppins", fontWeight: "bold" },
      siteName: { fontSize: 10, fontFamily: "Poppins" },
      headerRight: {
        fontSize: 12,
        fontFamily: "Poppins",
        fontStyle: "italic",
        fontWeight: "bold"
      },

      titleBar: {
        backgroundColor: "#39b54a",
        borderRadius: 4,
        paddingVertical: 4,
        marginBottom: 8
      },
      titleText: {
        color: "#fff",
        fontSize: 13,
        fontFamily: "Poppins",
        fontWeight: "bold",
        textAlign: "center"
      },

      sectionHead: {
        fontSize: 11,
        fontFamily: "Poppins",
        fontWeight: "bold",
        marginBottom: 5
      },
      grid: { flexDirection: "row", flexWrap: "wrap", marginBottom: 6 },
      rowItem: { width: "50%", flexDirection: "row", marginBottom: 3 },
      label: { width: 110, fontFamily: "Poppins" },
      value: { fontFamily: "Poppins", fontWeight: "bold" },

      card: {
        borderWidth: 1,
        borderColor: "#C8C8C8",
        borderRadius: 6,
        padding: 6,
        marginTop: 3
      },
      cardTitle: {
        fontSize: 11,
        fontFamily: "Poppins",
        fontWeight: "bold",
        marginBottom: 5
      },

      table: {
        borderWidth: 1,
        borderColor: "#C8C8C8",
        borderRadius: 4,
        overflow: "hidden",
        marginBottom: 6
      },
      tr: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#E6E6E6",
        minHeight: 18
      },
      th: {
        flex: 1,
        padding: 4,
        fontFamily: "Poppins",
        fontWeight: "bold",
        backgroundColor: "#EFEFEF",
        textAlign: "center",
        borderRightWidth: 1,
        borderRightColor: "#D0D0D0"
      },
      thFirst: {
        flex: 1.2,
        padding: 4,
        fontFamily: "Poppins",
        fontWeight: "bold",
        backgroundColor: "#EFEFEF",
        textAlign: "center",
        borderRightWidth: 1,
        borderRightColor: "#D0D0D0"
      },
      td: {
        flex: 1,
        padding: 4,
        fontFamily: "Poppins",
        textAlign: "center",
        borderRightWidth: 1,
        borderRightColor: "#F0F0F0"
      },
      tdFirst: {
        flex: 1.2,
        padding: 4,
        fontFamily: "Poppins",
        textAlign: "left",
        borderRightWidth: 1,
        borderRightColor: "#F0F0F0"
      },
      band: { backgroundColor: "#F6F6F6", fontWeight: "bold" },

      comments: {
        borderWidth: 1,
        borderColor: "#C8C8C8",
        borderRadius: 6,
        padding: 5,
        minHeight: 78,
        marginTop: 3
      },
      commentsHead: {
        fontFamily: "Poppins",
        fontWeight: "bold",
        marginBottom: 2
      },

      footer: {
        position: "absolute",
        left: 20,
        right: 20,
        bottom: 16,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center"
      },
      footLogo: { width: 58, height: 22 },
      pageNo: { fontSize: 10, fontFamily: "Poppins" },
      sign: { fontSize: 10, fontFamily: "Poppins" }
    });

    // Register fonts AFTER StyleSheet.create()
    Font.register({
      family: "Poppins",
      fonts: [
        { src: PoppinsRegular, fontWeight: 400 },
        { src: PoppinsBold, fontWeight: 700, fontStyle: "normal" },
        { src: PoppinsBold, fontWeight: 700, fontStyle: "italic" } // For italic + bold styles
      ]
    });

    const { exam, biometryData, siteName, hasLogo, siteLogo } = this.props;

    const patientDetails = this.safe(exam?.patientDetails, {});
    const age = patientDetails?.dateOfBirth
      ? Math.floor(
          (new Date() - new Date(patientDetails.dateOfBirth)) / 3.15576e10
        )
      : "";

    // data guards
    const R = biometryData?.rightEye || null;
    const L = biometryData?.leftEye || null;

    // ranges used on pages 2/3 (per your screenshots)
    const axialRanges = {
      cct: { label: "CCT", range: "Normal" }, // display only
      ad: { label: "ACD", range: "3.0–4.1 mm", min: 3.0, max: 4.1 },
      lt: { label: "LT", range: "3.5–4.0 mm", min: 3.5, max: 4.0 },
      vt: { label: "VT", range: "Normal" },
      al: { label: "AL", range: "22.0–25.0 mm", min: 22.0, max: 25.0 }
    };

    const keratoRanges = {
      k1: { label: "K1", range: "41.0–43.0 D" },
      k2: { label: "K2", range: "41.0–43.0 D" },
      ast: { label: "AST", range: "> -0.5 D" },
      avgR: { label: "AVG K", range: "Normal" },
      alCr: { label: "AL/CR", range: "2.96–3.06" }
    };

    const refractionRanges = {
      s: { label: "S", range: "Normal" },
      c: { label: "C", range: "Normal" },
      a: { label: "A", range: "Normal" }
    };

    const orgName = this.safe(sessionStorage.getItem("orgName"), "");
    const userName = this.safe(sessionStorage.getItem("userName"), "");

    // ------------- small components -------------
    const Header = () => (
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          {hasLogo ? (
            <View style={styles.orgLogoContainer}>
              <Image src={siteLogo} style={styles.orgLogo} />
            </View>
          ) : (
            <View
              style={[
                styles.orgLogoContainer,
                { borderWidth: 1, borderColor: "#DDD" }
              ]}
            />
          )}
          <View style={styles.orgDetails}>
            <Text style={styles.orgName}>{orgName}</Text>
            <Text style={styles.siteName}>{this.safe(siteName)}</Text>
          </View>
        </View>
        <Text style={styles.headerRight}>Remidio OBM</Text>
      </View>
    );

    const PatientDetails = () => (
      <>
        <Text style={styles.sectionHead}>PATIENT DETAILS</Text>
        <View style={styles.grid}>
          <View style={styles.rowItem}>
            <Text style={styles.label}>Name :</Text>
            <Text style={styles.value}>
              {this.safe(patientDetails.firstName, "")}{" "}
              {this.safe(patientDetails.lastName, "")}
            </Text>
          </View>
          <View style={styles.rowItem}>
            <Text style={styles.label}>Date:</Text>
            <Text style={styles.value}>
              {convertEpochToDate(new Date().getTime())}
            </Text>
          </View>
          <View style={styles.rowItem}>
            <Text style={styles.label}>MRN:</Text>
            <Text style={styles.value}>{this.safe(patientDetails.mrn)}</Text>
          </View>
          <View style={styles.rowItem}>
            <Text style={styles.label}>Age/ Gender:</Text>
            <Text style={styles.value}>
              {age !== "" ? `${age} yrs/ ` : ""}
              {patientDetails.gender
                ? patientDetails.gender === "MALE"
                  ? "M"
                  : "F"
                : ""}
            </Text>
          </View>
          <View style={styles.rowItem}>
            <Text style={styles.label}>Consultant:</Text>
            <Text style={styles.value}>{userName}</Text>
          </View>
        </View>
      </>
    );

    // ---------- PAGE 1 ----------
    const Page1 = () => (
      <Page size="A4" style={styles.page}>
        <Header />
        <View style={styles.titleBar}>
          <Text style={styles.titleText}>Optical Biometer Report</Text>
        </View>

        <PatientDetails />

        <View style={styles.card}>
          <Text style={styles.cardTitle}>RESULTS</Text>

          {/* Header */}
          <View style={styles.table}>
            <View style={[styles.tr, { backgroundColor: "#EFEFEF" }]}>
              <Text style={styles.thFirst}>Parameters</Text>
              <Text style={styles.th}>RIGHT EYE</Text>
              <Text style={styles.th}>LEFT EYE</Text>
            </View>

            {/* Axial band */}
            <View style={[styles.tr, styles.band]}>
              <Text style={styles.tdFirst}>Axial</Text>
              <Text style={styles.td} />
              <Text style={styles.td} />
            </View>

            {[
              {
                k: "cct",
                label: "CCT",
                r: this.formatCCT(R?.axialData?.cct),
                l: this.formatCCT(L?.axialData?.cct)
              },
              {
                k: "ad",
                label: "ACD",
                r: this.formatMM(R?.axialData?.ad),
                l: this.formatMM(L?.axialData?.ad),
                min: 3.0,
                max: 4.1
              },
              {
                k: "lt",
                label: "LT",
                r: this.formatMM(R?.axialData?.lt),
                l: this.formatMM(L?.axialData?.lt),
                min: 3.5,
                max: 4.0
              },
              {
                k: "vt",
                label: "VT",
                r: this.formatMM(R?.axialData?.vt),
                l: this.formatMM(L?.axialData?.vt)
              },
              {
                k: "al",
                label: "AL",
                r: this.formatMM(R?.axialData?.al),
                l: this.formatMM(L?.axialData?.al),
                min: 22.0,
                max: 25.0
              }
            ].map((row, idx) => (
              <View key={`ax-${idx}`} className="ax" style={styles.tr}>
                <Text style={styles.tdFirst}>{row.label}</Text>
                <Text
                  style={[
                    styles.td,
                    this.outStyle(Number(row.r), row.min, row.max)
                  ]}
                >
                  {row.r}
                </Text>
                <Text
                  style={[
                    styles.td,
                    this.outStyle(Number(row.l), row.min, row.max)
                  ]}
                >
                  {row.l}
                </Text>
              </View>
            ))}

            {/* Keratometry band */}
            <View style={[styles.tr, styles.band]}>
              <Text style={styles.tdFirst}>Keratometry</Text>
              <Text style={styles.td} />
              <Text style={styles.td} />
            </View>

            {[
              {
                label: "K1",
                r: this.formatKeratometryK1(R?.keratometryData?.k1),
                l: this.formatKeratometryK1(L?.keratometryData?.k1)
              },
              {
                label: "K2",
                r: this.formatKeratometryK2(R?.keratometryData?.k2),
                l: this.formatKeratometryK2(L?.keratometryData?.k2)
              },
              {
                label: "AST",
                r: this.formatKeratometryAST(R?.keratometryData?.ast),
                l: this.formatKeratometryAST(L?.keratometryData?.ast)
              },
              {
                label: "AVG K",
                r: this.formatAvgK(R?.keratometryData?.avgR),
                l: this.formatAvgK(L?.keratometryData?.avgR)
              },
              {
                label: "AL/ CR",
                r: this.formatALCR(R?.alCr),
                l: this.formatALCR(L?.alCr)
              }
            ].map((row, idx) => (
              <View key={`k-${idx}`} style={styles.tr}>
                <Text style={styles.tdFirst}>{row.label}</Text>
                <Text style={styles.td}>{row.r}</Text>
                <Text style={styles.td}>{row.l}</Text>
              </View>
            ))}

            {/* Refraction band */}
            <View style={[styles.tr, styles.band]}>
              <Text style={styles.tdFirst}>Refraction</Text>
              <Text style={styles.td} />
              <Text style={styles.td} />
            </View>

            {[
              {
                label: "S",
                r: this.formatRefractionS(R?.refractionData?.s),
                l: this.formatRefractionS(L?.refractionData?.s)
              },
              {
                label: "C",
                r: this.formatRefractionC(R?.refractionData?.c),
                l: this.formatRefractionC(L?.refractionData?.c)
              },
              {
                label: "A",
                r: this.formatRefractionA(R?.refractionData?.a),
                l: this.formatRefractionA(L?.refractionData?.a)
              }
            ].map((row, idx) => (
              <View key={`r-${idx}`} style={styles.tr}>
                <Text style={styles.tdFirst}>{row.label}</Text>
                <Text style={styles.td}>{row.r}</Text>
                <Text style={styles.td}>{row.l}</Text>
              </View>
            ))}

            {/* Additional */}
            {[
              {
                label: "PD",
                r: this.fmt(this.safe(R?.pwData?.pd, null), 1),
                l: ""
              },
              {
                label: "PS",
                r: this.safe(R?.refractionData?.s, ""),
                l: this.safe(L?.refractionData?.s, "")
              },
              {
                label: "WTW",
                r: this.fmt(this.safe(R?.pwData?.wtw, null), 2),
                l: this.fmt(this.safe(L?.pwData?.wtw, null), 2)
              }
            ].map((row, idx) => (
              <View key={`x-${idx}`} style={styles.tr}>
                <Text style={styles.tdFirst}>{row.label}</Text>
                <Text style={styles.td}>{row.r}</Text>
                <Text style={styles.td}>{row.l}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.comments}>
          <Text style={styles.commentsHead}>Comments :</Text>
        </View>

        <View style={styles.footer}>
          <Image src={Logo} style={styles.footLogo} />
          <Text style={styles.pageNo}>Page 1/3</Text>
          <Text style={styles.sign}>Dr. {userName}</Text>
        </View>
      </Page>
    );

    // ---------- Eye analysis table (pages 2/3) ----------
    const EyeAnalysis = ({ eyeLabel, E }) => {
      const ax = E?.axialData || {};
      const k = E?.keratometryData || {};
      const ref = E?.refractionData || {};
      const alCr = this.safe(E?.alCr, "");

      return (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{eyeLabel} | Complete Analysis</Text>

          <View style={styles.table}>
            {/* header */}
            <View style={[styles.tr, { backgroundColor: "#EFEFEF" }]}>
              <Text style={styles.thFirst}>Parameters</Text>
              <Text style={styles.th}>Result</Text>
              <Text style={styles.th}>Range</Text>
              <Text style={styles.th}>Analysis</Text>
            </View>

            {/* Axial band */}
            <View style={[styles.tr, styles.band]}>
              <Text style={styles.tdFirst}>Axial</Text>
              <Text style={styles.td} />
              <Text style={styles.td} />
              <Text style={styles.td} />
            </View>

            {Object.entries(axialRanges).map(([key, meta], idx) => {
              const val =
                key === "cct"
                  ? this.fmt(ax?.cct, 1)
                  : key === "ad"
                  ? this.fmt(ax?.ad, 2)
                  : key === "lt"
                  ? this.fmt(ax?.lt, 2)
                  : key === "vt"
                  ? this.fmt(ax?.vt, 2)
                  : key === "al"
                  ? this.fmt(ax?.al, 2)
                  : "";
              return (
                <View key={`ax2-${idx}`} style={styles.tr}>
                  <Text style={styles.tdFirst}>{meta.label}</Text>
                  <Text
                    style={[
                      styles.td,
                      this.outStyle(Number(val), meta.min, meta.max)
                    ]}
                  >
                    {val}
                  </Text>
                  <Text style={styles.td}>{meta.range}</Text>
                  <Text style={styles.td}>
                    {this.analysisForAxial(key, Number(val))}
                  </Text>
                </View>
              );
            })}

            {/* Keratometry band */}
            <View style={[styles.tr, styles.band]}>
              <Text style={styles.tdFirst}>Keratometry</Text>
              <Text style={styles.td} />
              <Text style={styles.td} />
              <Text style={styles.td} />
            </View>

            {[
              { key: "k1", val: this.safe(k?.k1, "") },
              { key: "k2", val: this.safe(k?.k2, "") },
              { key: "ast", val: this.safe(k?.ast, "") },
              { key: "avgR", val: this.fmt(k?.avgR, 0) },
              { key: "alCr", val: this.fmt(alCr, 2) }
            ].map((row, idx) => (
              <View key={`k2-${idx}`} style={styles.tr}>
                <Text style={styles.tdFirst}>
                  {keratoRanges[row.key]?.label || row.key.toUpperCase()}
                </Text>
                <Text style={styles.td}>{row.val}</Text>
                <Text style={styles.td}>
                  {keratoRanges[row.key]?.range || "Normal"}
                </Text>
                <Text style={styles.td}>
                  {this.analysisForK(row.key, row.val)}
                </Text>
              </View>
            ))}

            {/* Refraction band */}
            <View style={[styles.tr, styles.band]}>
              <Text style={styles.tdFirst}>Refraction</Text>
              <Text style={styles.td} />
              <Text style={styles.td} />
              <Text style={styles.td} />
            </View>

            {Object.entries(refractionRanges).map(([key, meta], idx) => {
              const val = this.safe(ref?.[key], "");
              return (
                <View key={`r2-${idx}`} style={styles.tr}>
                  <Text style={styles.tdFirst}>{meta.label}</Text>
                  <Text style={styles.td}>{val}</Text>
                  <Text style={styles.td}>{meta.range}</Text>
                  <Text style={styles.td}>Normal</Text>
                </View>
              );
            })}
          </View>
        </View>
      );
    };

    // ---------- PAGE 2 (RIGHT) ----------
    const Page2 = () => (
      <Page size="A4" style={styles.page}>
        <Header />
        <View style={styles.titleBar}>
          <Text style={styles.titleText}>Optical Biometer Report</Text>
        </View>

        <PatientDetails />

        <EyeAnalysis eyeLabel="RIGHT EYE" E={R} />

        <View style={styles.comments}>
          <Text style={styles.commentsHead}>Comments :</Text>
        </View>

        <View style={styles.footer}>
          <Image src={Logo} style={styles.footLogo} />
          <Text style={styles.pageNo}>Page 2/3</Text>
          <Text style={styles.sign}>Dr. {userName}</Text>
        </View>
      </Page>
    );

    // ---------- PAGE 3 (LEFT) ----------
    const Page3 = () => (
      <Page size="A4" style={styles.page}>
        <Header />
        <View style={styles.titleBar}>
          <Text style={styles.titleText}>Optical Biometer Report</Text>
        </View>

        <PatientDetails />

        <EyeAnalysis eyeLabel="LEFT EYE" E={L} />

        <View style={styles.comments}>
          <Text style={styles.commentsHead}>Comments :</Text>
        </View>

        <View style={styles.footer}>
          <Image src={Logo} style={styles.footLogo} />
          <Text style={styles.pageNo}>Page 3/3</Text>
          <Text style={styles.sign}>Dr. {userName}</Text>
        </View>
      </Page>
    );

    return (
      <Document>
        <Page1 />
        <Page2 />
        <Page3 />
      </Document>
    );
  }
}

export default BiometryPDF;
